/**
 * Momen Gallery Module
 * Handles polaroid and testimonial content switching with GSAP animations
 * Logic: Like stacking cards - non-looping with disabled buttons at ends
 */

import { gsap } from "gsap";

export function initMomenGallery() {
  const gallery = document.querySelector('.momen-gallery');
  if (!gallery) return;

  // Check if moment-photos exists (not moment-photos-simple)
  const photosContainer = gallery.querySelector('.moment-photos');
  if (!photosContainer) return;

  const polaroids = photosContainer.querySelectorAll('.polaroid');
  const contents = gallery.querySelectorAll('.moment-contents .content-item');
  const btnLeft = gallery.querySelector('.button-arrow.arrow--left');
  const btnRight = gallery.querySelector('.button-arrow.arrow--right');

  let currentIndex = 0;
  const totalItems = polaroids.length;
  const lastIndex = totalItems - 1;

  // Get transform values from CSS for each polaroid position
  function getPolaroidTransform(index) {
    const transforms = [
      { rotate: 0, x: 0, y: 0, zIndex: 10 },        // First
      { rotate: -12, x: -55, y: -90, zIndex: 9 },   // Second
      { rotate: 10, x: -88, y: 46, zIndex: 8 },     // Third
      { rotate: 3, x: 50, y: -55, zIndex: 7 }       // Fourth
    ];
    return transforms[index] || transforms[0];
  }

  // Update button states (disable/enable)
  function updateButtonStates() {
    if (btnLeft) {
      if (currentIndex === 0) {
        btnLeft.disabled = true;
        btnLeft.classList.add('disabled');
      } else {
        btnLeft.disabled = false;
        btnLeft.classList.remove('disabled');
      }
    }
    if (btnRight) {
      if (currentIndex === lastIndex) {
        btnRight.disabled = true;
        btnRight.classList.add('disabled');
      } else {
        btnRight.disabled = false;
        btnRight.classList.remove('disabled');
      }
    }
  }

  // Initialize - all polaroids visible on first load
  function init() {
    polaroids.forEach((polaroid, index) => {
      polaroid.classList.remove('hidden');
      if (index === 0) {
        polaroid.classList.add('active');
      } else {
        polaroid.classList.remove('active');
      }
    });
    updateButtonStates();
  }

  // Hide a polaroid (remove from stack) - animate up and fade out
  function hidePolaroid(index) {
    const polaroid = polaroids[index];
    gsap.to(polaroid, {
      opacity: 0,
      y: -80,
      duration: 0.4,
      ease: "power2.in",
      onStart: () => {
        polaroid.classList.remove('active');
      },
      onComplete: () => {
        polaroid.classList.add('hidden');
      }
    });
  }

  // Show a polaroid (add back to stack) - animate from below
  function showPolaroid(index) {
    const polaroid = polaroids[index];
    const transform = getPolaroidTransform(index);

    gsap.set(polaroid, {
      zIndex: transform.zIndex,
      rotation: transform.rotate,
      x: transform.x,
      y: transform.y + 50,
      scale: 0.95
    });

    gsap.to(polaroid, {
      opacity: 1,
      y: transform.y,
      scale: 1,
      duration: 0.5,
      ease: "power2.out",
      onStart: () => {
        polaroid.classList.remove('hidden');
      }
    });
  }

  // Animate to specific index
  function goToSlide(newIndex) {
    if (newIndex === currentIndex) return;
    if (newIndex < 0 || newIndex >= totalItems) return; // Prevent out of bounds

    const direction = newIndex > currentIndex ? 'next' : 'prev';
    const currentContent = contents[currentIndex];
    const newContent = contents[newIndex];

    // Create timeline for synchronized animation
    const tl = gsap.timeline({
      onComplete: () => {
        currentIndex = newIndex;
        updateButtonStates();
      }
    });

    // 1. Fade out current content
    tl.to(currentContent, {
      opacity: 0,
      duration: 0.3,
      ease: "power2.in",
      onComplete: () => {
        currentContent.classList.remove('active');
      }
    }, 0);

    // 2. Handle polaroid visibility based on direction
    if (direction === 'next') {
      // Going forward - hide the current polaroid
      tl.add(() => {
        hidePolaroid(currentIndex);
      }, 0);
    } else {
      // Going backward - show the previous polaroid
      tl.add(() => {
        showPolaroid(newIndex);
      }, 0.2);
    }

    // 3. Set active class on new polaroid
    tl.add(() => {
      polaroids.forEach(p => p.classList.remove('active'));
      polaroids[newIndex].classList.add('active');
    }, 0.2);

    // 4. Fade in new content
    tl.add(() => {
      newContent.classList.add('active');
    }, 0.3);

    tl.fromTo(newContent,
      { opacity: 0 },
      {
        opacity: 1,
        duration: 0.4,
        ease: "power2.out"
      },
      0.3
    );
  }

  // Event listeners
  if (btnRight) {
    btnRight.addEventListener('click', () => {
      if (currentIndex < lastIndex) {
        goToSlide(currentIndex + 1);
      }
    });
  }

  if (btnLeft) {
    btnLeft.addEventListener('click', () => {
      if (currentIndex > 0) {
        goToSlide(currentIndex - 1);
      }
    });
  }

  // Initialize
  init();
}

/**
 * Momen Gallery Simple Module
 * Simplified version for .moment-photos-simple layout
 * Only fade animation for .polaroid .item based on active .content-item
 */
export function initMomenGallerySimple() {
  const gallery = document.querySelector('.momen-gallery');
  if (!gallery) return;

  const photosSimple = gallery.querySelector('.moment-photos-simple');
  if (!photosSimple) return;

  const polaroidItems = photosSimple.querySelectorAll('.polaroid .item');
  const contents = gallery.querySelectorAll('.moment-contents .content-item');
  const btnLeft = gallery.querySelector('.button-arrow.arrow--left');
  const btnRight = gallery.querySelector('.button-arrow.arrow--right');

  let currentIndex = 0;
  const totalItems = polaroidItems.length;
  const lastIndex = totalItems - 1;

  // Update button states (disable/enable)
  function updateButtonStates() {
    if (btnLeft) {
      if (currentIndex === 0) {
        btnLeft.disabled = true;
        btnLeft.classList.add('disabled');
      } else {
        btnLeft.disabled = false;
        btnLeft.classList.remove('disabled');
      }
    }
    if (btnRight) {
      if (currentIndex === lastIndex) {
        btnRight.disabled = true;
        btnRight.classList.add('disabled');
      } else {
        btnRight.disabled = false;
        btnRight.classList.remove('disabled');
      }
    }
  }

  // Initialize - show only first item
  function init() {
    polaroidItems.forEach((item, index) => {
      if (index === 0) {
        item.classList.add('active');
        gsap.set(item, { opacity: 1 });
      } else {
        item.classList.remove('active');
        gsap.set(item, { opacity: 0 });
      }
    });
    updateButtonStates();
  }

  // Animate to specific index with simple fade
  function goToSlide(newIndex) {
    if (newIndex === currentIndex) return;
    if (newIndex < 0 || newIndex >= totalItems) return;

    const currentContent = contents[currentIndex];
    const newContent = contents[newIndex];
    const currentItem = polaroidItems[currentIndex];
    const newItem = polaroidItems[newIndex];

    // Create timeline for synchronized animation
    const tl = gsap.timeline({
      onComplete: () => {
        currentIndex = newIndex;
        updateButtonStates();
      }
    });

    // 1. Fade out current content
    tl.to(currentContent, {
      opacity: 0,
      duration: 0.3,
      ease: "power2.in",
      onComplete: () => {
        currentContent.classList.remove('active');
      }
    }, 0);

    // 2. Fade out current polaroid item
    tl.to(currentItem, {
      opacity: 0,
      duration: 0.3,
      ease: "power2.in",
      onComplete: () => {
        currentItem.classList.remove('active');
      }
    }, 0);

    // 3. Fade in new polaroid item
    tl.add(() => {
      newItem.classList.add('active');
    }, 0.3);

    tl.to(newItem, {
      opacity: 1,
      duration: 0.4,
      ease: "power2.out"
    }, 0.3);

    // 4. Fade in new content
    tl.add(() => {
      newContent.classList.add('active');
    }, 0.3);

    tl.fromTo(newContent,
      { opacity: 0 },
      {
        opacity: 1,
        duration: 0.4,
        ease: "power2.out"
      },
      0.3
    );
  }

  // Event listeners
  if (btnRight) {
    btnRight.addEventListener('click', () => {
      if (currentIndex < lastIndex) {
        goToSlide(currentIndex + 1);
      }
    });
  }

  if (btnLeft) {
    btnLeft.addEventListener('click', () => {
      if (currentIndex > 0) {
        goToSlide(currentIndex - 1);
      }
    });
  }

  // Initialize
  init();
}

